import React, { useState, useEffect } from "react";
import styles from "./CustomSelect.module.scss";

interface Option {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CustomSelectProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  options?: Option[];
  type?: "normal" | "state" | "timezone" | "language";
  required?: boolean;
  error?: string;
  size?: "full" | "half" | "third";
  disabled?: boolean;
}

const stateOptions: Option[] = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" }
];

const timezoneOptions: Option[] = [
  { value: "America/New_York", label: "America/New_York" },
  { value: "America/Chicago", label: "America/Chicago" },
  { value: "America/Denver", label: "America/Denver" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles" },
  { value: "Europe/London", label: "Europe/London" },
  { value: "Europe/Paris", label: "Europe/Paris" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo" },
  { value: "Australia/Sydney", label: "Australia/Sydney" }
];

const languageOptions: Option[] = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "zh", label: "Chinese" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "ru", label: "Russian" },
  { value: "pt", label: "Portuguese" },
  { value: "ar", label: "Arabic" }
];

const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  value,
  onChange,
  options = [],
  type = "normal",
  required = false,
  error = "",
  size = "full",
  disabled = false
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [internalOptions, setInternalOptions] = useState<Option[]>([]);

  useEffect(() => {
    if (type === "state") setInternalOptions(stateOptions);
    else if (type === "timezone") setInternalOptions(timezoneOptions);
    else if (type === "language") setInternalOptions(languageOptions);
    else setInternalOptions(options);
  }, [type, options]);

  useEffect(() => {
    if (type === "language") {
      const validLanguageValues = languageOptions.map(opt => opt.value.toLowerCase());
      const cleanedValue = (value || "").toLowerCase();

      if (!validLanguageValues.includes(cleanedValue)) {
        onChange("en");
      }
    }
  }, [type, value, onChange]);

  return (
    <div
      className={`${styles.selectWrapper} ${styles[size]} ${
        isFocused ? styles.focused : ""
      } ${error ? styles.error : ""}`}
    >
      <label
        className={`${styles.floatingLabel} ${value ? styles.filled : ""}`}
      >
        {label} {required && "*"}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={disabled}
      >
        <option value="">Select...</option>
        {internalOptions.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled || false}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && <div className={styles.errorMessage}>{error}</div>}
    </div>
  );
};

export default CustomSelect;